import { createSlice, createSelector } from '@reduxjs/toolkit';
import { ensureArray } from '../common/utils/arrayUtilities';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';

const compareTypes = (t1, t2) => t1.internalid === t2.internalid;

const typeSlice = createSlice({
    name: 'types',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const t of ensureArray(action.payload)) {
                let idx = state.data.findIndex(t2 => compareTypes(t, t2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, t);
                } else {
                    state.data.push(t);
                }
            }
        },
        remove: (state, action) => {
            for (const t of ensureArray(action.payload)) {
                let idx = state.data.findIndex(t2 => compareTypes(t2, t));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => typeSlice.getInitialState());
    }
});

// #region network operations
const fetchTypes = network => {
    return network.get('/api/v1/types', typeSlice.actions.set);
}
export const createType = (network, data) => network.post('/api/v1/type', data, typeSlice.actions.addOrUpdate);
export const updateType = (network, data) => network.put('/api/v1/type', data, typeSlice.actions.addOrUpdate);
export const removeType = (network, data) => network.delete('/api/v1/type/' + encodeURIComponent(data.internalid), typeSlice.actions.remove);
// #endregion

// #region selectors
const selectTypes = state => state.types.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectTypeByName = createSelector([selectTypes, (types, site) => site, (types, site, level) => level, (types, site, level, type) => type], (types, site, level, type) => {
    // we do the if here to make sure we always match the most specific
    let result = types.find(t => (!site || t.site === site) && (!level || t.level === level) && t.type === type)
    if (!result) {
        result = types.find(t => (!site || t.site === site) && !t.level && t.type === type);
    }
    if (!result) {
        result = types.find(t => !t.site && (!level || t.level === level) && t.type === type);
    }
    if (!result) {
        result = types.find(t => !t.site && !t.level && t.type === type);
    }
    return result;
});
const selectType = (site, level, name) => state => selectTypeByName(state, site, level, name);
const selectTypeBySiteAndLevel = createSelector([selectTypes, (types, site) => site, (types, site, level) => level], (types, site, level) => {
    return types.filter(t => (site == null || t.site == null || t.site === site) && (level == null || t.level == null || t.level === level))
});
const selectTypeForSiteLevel = (site, level) => state => selectTypeBySiteAndLevel(state, site, level);
// #endregion

// #region listeners
const listen = {
    typeCreated: (type, { dispatch }) => dispatch(typeSlice.actions.addOrUpdate(type)),
    typeUpdated: (type, { dispatch }) => dispatch(typeSlice.actions.addOrUpdate(type)),
    typeRemoved: (type, { dispatch }) => dispatch(typeSlice.actions.remove(type)),
}
// #endregion

// #region permission checks
export const canCreateType = (credentials, type) => credentials.access.hasFull('administrator');
export const canUpdateType = (credentials, type) => credentials.access.hasFull('administrator');
export const canRemoveType = (credentials, type) => credentials.access.hasFull('administrator');
// #endregion
const typeInit = { run: fetchTypes, listen, authenticated: false, immortal: true };
export const [useTypes] = dataHookFactory(selectTypes, typeInit);
export const [useType,realizeType] = dataHookFactory(selectType, (site, level, type) => typeInit);
export const [useTypesForSiteLevel] = dataHookFactory(selectTypeForSiteLevel, (site, level) => typeInit);

const reducerDetails = { name: typeSlice.name, reducer: typeSlice.reducer };
export default reducerDetails;